import React from 'react';
import {
  FileExcelOutlined, FileImageOutlined, FileOutlined, FilePdfOutlined,
  FilePptOutlined, FileTextOutlined, FileWordOutlined, FileZipOutlined,
  FolderOutlined, ProjectOutlined,
} from '@ant-design/icons';

import { IDriveNode } from '../../../types/models/pathfinder';

export interface FileKind {
  /** Short human label for the card and the detail modal. */
  label: string;
  icon: React.ReactNode;
  /** Drives the icon tint only. Nothing about it is a status. */
  color: string;
}

/**
 * Mime type in, icon out.
 *
 * This stands in for the thumbnails a file browser would normally show.
 * Pathfinder stores no `thumbnailLink` — by design, "no vision or image
 * rendering" is one of its hard boundaries — and Drive's own thumbnail URLs
 * are short-lived and auth-gated, so they cannot go in an `<img src>` from
 * this app anyway. Real previews mean fetching bytes through `/v1/fetch` and
 * rendering them client-side, which is a larger piece of work than this page.
 * Until then, an honest icon beats a broken image.
 *
 * CAD gets its own kind because floor plans are the reason this search exists,
 * and `.dwg` arriving as `application/octet-stream` from Drive would otherwise
 * be indistinguishable from a random binary.
 */
const KINDS: Array<{ match: (mime: string, name: string) => boolean; kind: FileKind }> = [
  {
    match: mime => mime === 'application/pdf',
    kind: { label: 'PDF', icon: <FilePdfOutlined />, color: '#cf1322' },
  },
  {
    match: (mime, name) => /\.(dwg|dxf|rvt|skp)$/i.test(name)
      || mime.includes('dwg') || mime.includes('acad') || mime.includes('dxf'),
    kind: { label: 'CAD drawing', icon: <ProjectOutlined />, color: '#531dab' },
  },
  {
    match: mime => mime.startsWith('image/'),
    kind: { label: 'Image', icon: <FileImageOutlined />, color: '#08979c' },
  },
  {
    match: mime => mime.includes('spreadsheet') || mime.includes('excel') || mime === 'text/csv',
    kind: { label: 'Spreadsheet', icon: <FileExcelOutlined />, color: '#389e0d' },
  },
  {
    match: mime => mime.includes('presentation') || mime.includes('powerpoint'),
    kind: { label: 'Presentation', icon: <FilePptOutlined />, color: '#d46b08' },
  },
  {
    match: mime => mime.includes('document') || mime.includes('word'),
    kind: { label: 'Document', icon: <FileWordOutlined />, color: '#096dd9' },
  },
  {
    match: mime => mime.includes('zip') || mime.includes('compressed') || mime.includes('tar'),
    kind: { label: 'Archive', icon: <FileZipOutlined />, color: '#7cb305' },
  },
  {
    match: mime => mime.startsWith('text/'),
    kind: { label: 'Text', icon: <FileTextOutlined />, color: '#595959' },
  },
];

const FOLDER: FileKind = { label: 'Folder', icon: <FolderOutlined />, color: '#d48806' };
const UNKNOWN: FileKind = { label: 'File', icon: <FileOutlined />, color: '#8c8c8c' };

export const kindOf = (node: IDriveNode): FileKind => {
  if (node.type === 'folder') return FOLDER;

  const mime = (node.mime_type || '').toLowerCase();
  const name = node.name || '';
  return KINDS.find(k => k.match(mime, name))?.kind || UNKNOWN;
};

/**
 * The path minus the leaf, for showing where a result lives.
 *
 * Two results named "Planta baja.pdf" are only distinguishable by this, which
 * is why the grid shows it on every card rather than on hover.
 */
export const parentPath = (node: IDriveNode): string => {
  const cut = (node.path || '').lastIndexOf('/');
  return cut > 0 ? node.path.slice(0, cut) : '';
};
